import { useState, useEffect } from "react";
import { Box, Button, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import GridIndex from "../../../components/GridIndex";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../../services/Api";
import moment from "moment";
import useBreadcrumbs from "../../../hooks/useBreadcrumbs";
import { Check, HighlightOff } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CustomModal from "../../../components/CustomModal";
import DailyPlannerStudentListDialog from "./studentListDialog";
import {
    extractDailyPlannerContent,
    hasDailyPlannerTaskStarted,
} from "./dailyPlannerUtils";

const isAttendanceTaken = (row, takenIds) =>
    row?.attendance_status === true ||
    row?.attendance_status === 1 ||
    takenIds?.has(row?.id);

const userID = JSON.parse(sessionStorage.getItem("AcharyaErpUser"))?.userId;

const TaskList = () => {
    const [rows, setRows] = useState([]);
    const setCrumbs = useBreadcrumbs();
    const navigate = useNavigate();
    const location = useLocation();
    const [modalContent, setModalContent] = useState({
        title: "",
        message: "",
        buttons: [],
    });
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [studentListItem, setStudentListItem] = useState(null);
    const [attendanceTakenIds, setAttendanceTakenIds] = useState(() => new Set());

    const handleViewStudents = (row) => {
        setStudentListItem(row);
    };

    const getAttendanceAction = (row) => {
        if (isAttendanceTaken(row, attendanceTakenIds)) {
            return {
                disabled: false,
                view: true,
                tooltip: "View attendance",
            };
        }

        if (row.is_approved !== true) {
            return {
                disabled: true,
                view: false,
                tooltip: "Approve this task to mark attendance",
            };
        }

        if (!row.student_count) {
            return {
                disabled: true,
                view: false,
                tooltip: "Add students before marking attendance",
            };
        }

        if (!hasDailyPlannerTaskStarted(row)) {
            return {
                disabled: true,
                view: false,
                tooltip: "Attendance can be marked after the task start time",
            };
        }

        return {
            disabled: false,
            view: false,
            tooltip: "Mark attendance",
        };
    };

    const columns = [
        {
            field: "empcode",
            headerName: "Emp Code",
            flex: 1,
            hide: true,
            valueGetter: (value, row) => row.empcode,
        },
        {
            field: "type",
            headerName: "Type",
            flex: 1,
            valueGetter: (value, row) => row.type,
        },
        {
            field: "task_title",
            headerName: "Title",
            flex: 1,
            valueGetter: (value, row) => row.task_title,
        },
        {
            field: "task_type",
            headerName: "Task Type",
            flex: 1,
            valueGetter: (value, row) => row.task_type,
        },
        {
            field: "description",
            headerName: "Description",
            flex: 1,
            valueGetter: (value, row) => row.description,
        },
        {
            field: "from_date",
            headerName: "From Date",
            flex: 1,
            valueGetter: (value, row) => `${row.from_date} ${moment(row.from_time, "h:mm A").format("h:mm A")}`,
        },
        {
            field: "to_date",
            headerName: "To Date",
            flex: 1,
            valueGetter: (value, row) => `${row.to_date} ${moment(row.to_time, "h:mm A").format("h:mm A")}`,
        },
        {
            field: "contribution_type",
            headerName: "Contribution Type",
            flex: 1,
            hide: true,
            valueGetter: (value, row) => row.contribution_type,
        },
        {
            field: "task_priority",
            headerName: "Priority",
            flex: 1,
            valueGetter: (value, row) => row.task_priority,
        },
        {
            field: "students",
            headerName: "Students",
            flex: 1,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => {
                if (params.row.student_status !== true) {
                    return <Typography sx={{ color: "text.secondary" }}>—</Typography>;
                }

                const count = params.row.student_count || 0;
                const isApproved = params.row.is_approved === true;

                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Button
                            variant="text"
                            size="small"
                            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                            disabled={count === 0}
                            onClick={() => handleViewStudents(params.row)}
                        >
                            {count}
                        </Button>
                        <Tooltip
                            title={
                                isApproved
                                    ? "Cannot add students after approval"
                                    : "Add students"
                            }
                            arrow
                        >
                            <span>
                                <IconButton
                                    color="primary"
                                    size="small"
                                    disabled={isApproved}
                                    onClick={() =>
                                        navigate(`/daily-planner/add-student/${params.row.id}`)
                                    }
                                >
                                    <PersonAddAltIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            field: "attendance",
            headerName: "Attendance",
            flex: 1,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => {
                if (params.row.student_status !== true) {
                    return <Typography sx={{ color: "text.secondary" }}>—</Typography>;
                }

                const action = getAttendanceAction(params.row);
                const Icon = action.view ? VisibilityIcon : HowToRegIcon;

                return (
                    <Tooltip title={action.tooltip} arrow>
                        <span>
                            <IconButton
                                color="primary"
                                size="small"
                                disabled={action.disabled}
                                onClick={() =>
                                    navigate(`/daily-planner/attendance/${params.row.id}`)
                                }
                            >
                                <Icon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: "is_approved",
            headerName: "Approval",
            flex: 1,
            renderCell: (params) => {
                if (params.row.student_status !== true) {
                    return <Typography sx={{ color: "text.secondary" }}>—</Typography>;
                }

                return params.row.is_approved === true ? (
                    <Chip label="Approved" color="success" size="small" variant="outlined" />
                ) : (
                    <Chip label="Pending" color="warning" size="small" variant="outlined" />
                );
            },
        },
        {
            field: "task_status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) =>
                params.row.task_status === "Pending" ? (
                    <Button variant="text" sx={{ color: "red" }} onClick={() => handleStatus(params)}>
                        {params.row.task_status}
                    </Button>
                ) : (
                    <Typography sx={{ color: "green" }}>{params.row.task_status}</Typography>
                ),
        },
        {
            field: "active",
            headerName: "Active",
            flex: 1,
            type: "actions",
            getActions: (params) => [
                params.row.active === true ? (
                    <IconButton
                        style={{ color: "green" }}
                        onClick={() => handleActive(params)}
                    >
                        <Check />
                    </IconButton>
                ) : (
                    <IconButton
                        style={{ color: "red" }}
                        onClick={() => handleActive(params)}
                    >
                        <HighlightOff />
                    </IconButton>
                ),
            ],
        },
    ];

    useEffect(() => {
        setCrumbs([
            { name: "Daily Planner" },
        ]);
        getData();
    }, []);

    useEffect(() => {
        const takenId = Number(location.state?.attendanceTakenId);
        if (!takenId) return;

        setAttendanceTakenIds((prev) => {
            const next = new Set(prev);
            next.add(takenId);
            return next;
        });
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, location.pathname, navigate]);

    const getData = async () => {
        const empId = await getEmpId();
        if (empId === null) return;
        setLoading(true);
        await axios
            .get(`/api/fetchAllDailyPlannerBasedOnUser?page=0&page_size=1000&sort=created_date&userId=${userID}`)
            .then((res) => {
                setLoading(false);
                setRows(extractDailyPlannerContent(res.data));
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    };

    const getEmpId = async () => {
        return new Promise((resolve) => {
            setLoading(true);
            const userId = JSON.parse(sessionStorage.getItem("AcharyaErpUser"))?.userId;
            axios.get(`/api/employee/getEmployeeDetailsByUserID/${userId}`)
                .then((res) => {
                    setLoading(false);
                    if (res.data.data !== null) resolve(res.data.data.emp_id);
                    else resolve(null);
                })
                .catch((err) => {
                    setLoading(false);
                    console.error(err);
                    resolve(null);
                });
        });
    };

    const handleActive = async (params) => {
        const id = params.row.id;
        const handleToggle = async () => {
            setLoading(true);
            if (params.row.active === true) {
                await axios
                    .delete(`/api/deactiveDailyPlanner/${id}`)
                    .then((res) => {
                        setLoading(false);
                        if (res.status === 200) {
                            getData();
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                        setLoading(false);
                    });
            } else if (params.row.active === false) {
                await axios
                    .delete(`/api/activateDailyPlanner/${id}`)
                    .then((res) => {
                        setLoading(false);
                        if (res.status === 200) {
                            getData();
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                        setLoading(false);
                    });
            }
        };
        params.row.active === true
            ? setModalContent({
                title: "",
                message: "Do you want to make it Inactive ?",
                buttons: [
                    { name: "Yes", color: "primary", func: handleToggle },
                    { name: "No", color: "primary", func: () => { } },
                ],
            })
            : setModalContent({
                title: "",
                message: "Do you want to make it Active ?",
                buttons: [
                    { name: "Yes", color: "primary", func: handleToggle },
                    { name: "No", color: "primary", func: () => { } },
                ],
            });
        setModalOpen(true);
    };

    const handleStatus = (params) => {
        const handleToggle = async () => {
            setLoading(true);
            const { id, from_date, active, description, task_priority, to_date, task_title, to_time, from_time, emp_id, task_type, contribution_type, type } = params.row;
            const body = {
                "daily_planner_id": id,
                "emp_id": emp_id,
                "task_title": task_title,
                "task_priority": task_priority,
                "description": description,
                "task_status": "Completed",
                "from_date": from_date,
                "to_date": to_date,
                "from_time": from_time,
                "to_time": to_time,
                "active": active,
                "type": type,
                "task_type": task_type,
                "contribution_type": contribution_type,
                "student_status": params.row.student_status === true,
            };
            await axios
                .put(`/api/updateDailyPlanner/${id}`, body)
                .then((res) => {
                    setLoading(false);
                    if (res.status === 200) {
                        getData();
                    }
                })
                .catch((err) => {
                    console.error(err);
                    setLoading(false);
                });
        };
        setModalContent({
            title: "",
            message: "Do you want to make it Complete?",
            buttons: [
                { name: "Yes", color: "primary", func: handleToggle },
                { name: "No", color: "primary", func: () => { } },
            ],
        });
        setModalOpen(true);
    };

    return (
        <>
            <CustomModal
                open={modalOpen}
                setOpen={setModalOpen}
                title={modalContent.title}
                message={modalContent.message}
                buttons={modalContent.buttons}
            />
            <DailyPlannerStudentListDialog
                item={studentListItem}
                open={Boolean(studentListItem)}
                setOpen={(nextOpen) => {
                    if (!nextOpen) setStudentListItem(null);
                }}
            />
            <Box sx={{ position: "relative", mt: 2 }}>
                <Button
                    onClick={() => navigate("/daily-planner/create")}
                    variant="contained"
                    disableElevation
                    sx={{ position: "absolute", right: 0, top: -50, borderRadius: 2 }}
                    startIcon={<AddIcon />}
                >
                    Create
                </Button>
                <GridIndex rows={rows} columns={columns} loading={loading} />
            </Box>
        </>
    );
};
export default TaskList;
